// ═══════════════════════════════════════════════════════════════════════════════
// WEEKLY WINDOW UTILITY
// ═══════════════════════════════════════════════════════════════════════════════
// The PPUSA weekly cycle runs Wednesday 12:00 AM Eastern → Tuesday 11:59 PM
// Eastern. This module computes the window boundaries and the week label used
// for presentation history, snapshot keys, and UI scoping.
//
// Eastern time is resolved through the IANA zone (America/New_York) rather
// than a fixed UTC-5 offset. The previous version hardcoded -5 and then read
// the result with LOCAL date getters, so from March through November — while
// Eastern is on daylight time at UTC-4 — every label landed one day late.
// Because presentation_history upserts on week_label, the same real week got
// saved under two different labels ("Week of 5/26" and "Week of 5/27"), which
// is what put duplicate weeks in the history dropdown.

const ZONE = "America/New_York";
const DAY_MS = 86_400_000;
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const partsFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: ZONE,
  weekday: "short", year: "numeric", month: "numeric", day: "numeric",
  hour: "numeric", minute: "numeric", second: "numeric", hour12: false,
});

// Wall-clock fields for an instant, as seen in Eastern time.
function easternParts(date) {
  const out = {};
  for (const { type, value } of partsFmt.formatToParts(date)) {
    if (type !== "literal") out[type] = value;
  }
  return {
    year: +out.year,
    month: +out.month,
    day: +out.day,
    hour: +out.hour % 24,          // Intl can emit 24 for midnight
    minute: +out.minute,
    second: +out.second,
    dow: DOW[out.weekday],
  };
}

// The UTC instant for a given Eastern wall-clock time. Eastern's offset
// depends on the date, so solve for it: guess with a fixed offset, read back
// what that instant looks like in Eastern, and correct by the drift. One
// correction pass is enough — a second confirms it across DST boundaries.
function easternWallClockToUtc(year, month, day, hour, minute, second, ms = 0) {
  let guess = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  for (let i = 0; i < 2; i++) {
    const p = easternParts(new Date(guess));
    const seenAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, ms);
    const wantedAsUtc = Date.UTC(year, month - 1, day, hour, minute, second, ms);
    const drift = wantedAsUtc - seenAsUtc;
    if (drift === 0) break;
    guess += drift;
  }
  return new Date(guess);
}

/**
 * Returns { weekStart, weekEnd, weekLabel } for the weekly cycle containing
 * the given date (defaults to now).
 *
 * weekStart = most recent Wednesday 00:00:00.000 Eastern
 * weekEnd   = that week's Tuesday 23:59:59.999 Eastern
 * weekLabel = "Week of M/D" using the Tuesday date (the meeting day)
 */
export function getCurrentWeekWindow(now = new Date()) {
  const p = easternParts(now);

  // Days back to the most recent Wednesday (dow 3). Wednesday itself = 0.
  const daysBack = (p.dow - 3 + 7) % 7;

  // Walk to the Wednesday in Eastern calendar terms, then pin midnight.
  const wedProbe = new Date(now.getTime() - daysBack * DAY_MS);
  const wp = easternParts(wedProbe);
  const weekStart = easternWallClockToUtc(wp.year, wp.month, wp.day, 0, 0, 0, 0);

  // Tuesday is six calendar days after that Wednesday.
  const tueProbe = new Date(weekStart.getTime() + 6 * DAY_MS + 12 * 3600 * 1000);
  const tp = easternParts(tueProbe);
  const weekEnd = easternWallClockToUtc(tp.year, tp.month, tp.day, 23, 59, 59, 999);

  return {
    weekStart,
    weekEnd,
    weekLabel: `Week of ${tp.month}/${tp.day}`,
    // Sortable key for the week itself. presentation_history orders on this
    // so the dropdown reads chronologically — ordering by presented_at put
    // weeks out of sequence whenever one was presented late.
    weekStartDate: `${wp.year}-${String(wp.month).padStart(2, "0")}-${String(wp.day).padStart(2, "0")}`,
  };
}

/** ISO string for the current week's start (Wednesday 00:00 Eastern). */
export function getWeekStartISO(now = new Date()) {
  return getCurrentWeekWindow(now).weekStart.toISOString();
}

/** ISO string for the current week's end (Tuesday 23:59:59.999 Eastern). */
export function getWeekEndISO(now = new Date()) {
  return getCurrentWeekWindow(now).weekEnd.toISOString();
}
